/**
 * Back office's own permission check, copied from `fsim/common/middlewares/permissions.py`
 * (`PermissionResolver`) as `BOAuthMiddleware` runs it with `allow_superuser = False`, so the
 * Permissions page offers only what back office will accept. Control's `checkPermission` differs
 * on purpose elsewhere: it honours `*:*` and lets a wildcard reach an `:edit` permission.
 *
 * - `*:*` grants nothing.
 * - A grant with fewer than two sections grants nothing beyond itself.
 * - `*` in a grant matches zero or more sections: `backoffice:*` and `backoffice:iam:groups:*`
 *   both grant `backoffice:iam:groups`.
 * - A permission ending in `:edit` is granted only by a grant that also ends in `:edit`, so
 *   `backoffice:*` never grants `backoffice:iam:groups:edit`.
 */
const SUPERUSER = '*:*';
const EDIT_SECTION = 'edit';

function globMatch(pattern: readonly string[], target: readonly string[]): boolean {
  if (pattern.length === 0) return target.length === 0;
  const [head, ...rest] = pattern;
  if (head === '*') {
    return globMatch(rest, target) || (target.length > 0 && globMatch(pattern, target.slice(1)));
  }
  return target.length > 0 && target[0] === head && globMatch(rest, target.slice(1));
}

function grants(pattern: readonly string[], target: readonly string[]): boolean {
  if (target[target.length - 1] === EDIT_SECTION && pattern[pattern.length - 1] !== EDIT_SECTION) {
    return false;
  }
  return globMatch(pattern, target);
}

/** Whether back office would let these grants call an endpoint that requires `required`. */
export function backOfficeGrants(required: string, held: readonly string[] | undefined): boolean {
  return (held ?? []).some((grant) => {
    if (grant === required) return true;
    if (grant === SUPERUSER) return false;
    const sections = grant.split(':');
    return sections.length >= 2 && grants(sections, required.split(':'));
  });
}
